import React, { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Telescope, Network, BrainCircuit } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import "./About.css"; // We'll update this CSS

export default function About() {
  const [showSkyPreview, setShowSkyPreview] = useState(false);

  const stellariumPlatforms = [
    {
      title: "Stellarium Mobile",
      desc: "A portable planetarium app for iOS and Android that simulates a realistic sky with stars, planets, and constellations.",
      image: "/stellarium_mobile.png",
    },
    {
      title: "Stellarium Web",
      desc: "The browser-based version of Stellarium that lets you explore the cosmos online without installation.",
      image: "/stellarium_web.png",
    },
    {
      title: "Stellarium Desktop",
      desc: "The open-source, full-featured planetarium software for Windows, macOS, and Linux systems.",
      image: "/stellarium_desktop.png",
    },
  ];

  return (
    <PageWrapper>
      <div className="flex flex-col items-center justify-center min-h-screen text-white about-page font-Times New Roman">
        
        {/* 🌌 Hero Section */}
        <div
          className="about-hero w-full h-[40vh] flex flex-col justify-center items-center text-center bg-cover bg-center px-4 sm:px-6 md:px-8" // Responsive padding
          style={{ backgroundImage: "url('/images/stellarium_bg.jpg')" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="hero-inner"
          >
            <h1 className="mb-2 text-3xl font-bold text-white sm:text-4xl md:text-5xl"> {/* Responsive text size */}
              STELLARIUM
            </h1>
            <p className="max-w-lg mx-auto text-base leading-tight text-white sm:text-lg sm:max-w-xl md:max-w-4xl"> {/* Responsive text size and max-width */}
              Stellarium is an open-source planetarium software that displays a realistic night sky in 3D-just like what you see with the naked eye, binoculars, or a telescope. Available across multiple platforms, Stellarium brings the universe to your fingertips.
            </p>
          </motion.div>
        </div>

        {/* 🚀 Stellarium Platforms */}
        <section className="w-full px-4 py-8 sm:px-6 md:px-8 platforms-section"> {/* Responsive padding and vertical spacing */}
          <h2 className="mb-6 text-2xl font-bold text-center text-white sm:text-3xl"> {/* Responsive text size */}
            Stellarium Platforms
          </h2>

          <div className="grid max-w-6xl grid-cols-1 gap-8 mx-auto md:grid-cols-2 lg:grid-cols-3"> {/* Responsive grid */}
            {stellariumPlatforms.map((platform, index) => (
              <motion.div
                key={index}
                className="flex flex-col platform-card" // Added flex flex-col to keep content aligned
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <img src={platform.image} alt={platform.title} className="object-cover w-full h-48 mb-4 rounded-t-lg" /> {/* Adjusted image size and styling */}
                <div className="flex flex-col flex-grow p-4 text-center"> {/* Added padding */}
                  <h3 className="mb-2 text-xl font-semibold">{platform.title}</h3> {/* Responsive text size */}
                  <p className="flex-grow text-base">{platform.desc}</p> {/* Responsive text size */}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 🔭 Lumora Integration */}
        <section className="px-4 py-8 sm:px-6 md:px-8 lg:py-12 features-section"> {/* Responsive padding and vertical spacing */}
          <motion.h2
            className="flex flex-col items-center justify-center gap-2 mb-6 text-4xl font-bold text-center text-white sm:flex-row sm:text-5xl md:text-6xl" // Responsive text size and flex direction
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <Network className="mb-2 text-white sm:mb-0" size={32} /> How Lumora Integrates Stellarium
          </motion.h2>

          <motion.p
            className="max-w-xl mx-auto mb-8 text-base leading-relaxed text-center text-white sm:max-w-2xl md:max-w-3xl sm:text-lg" // Responsive text size and max-width
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Lumora leverages <strong className="text-white">Stellarium’s real-time astronomical data</strong>
            to power its AI-assisted learning experience. Using Stellarium’s celestial
            database, Lumora fetches azimuth, altitude, and object metadata, enabling
            interactive tracking, multilingual narration, and visually enriched space education.
          </motion.p>

          <div className="grid max-w-6xl grid-cols-1 gap-8 mx-auto sm:grid-cols-2 lg:grid-cols-3"> {/* Responsive grid */}
            {[
              {
                icon: <BrainCircuit className="mx-auto mb-2 text-white" size={32} />,
                title: "AI-Assisted Discovery",
                desc: "Lumora uses AI to interpret celestial data from Stellarium, offering easy-to-understand explanations.",
              },
              {
                icon: <Telescope className="mx-auto mb-2 text-white" size={32} />,
                title: "Real-Time Tracking",
                desc: "Fetches live azimuth and altitude data for precise celestial tracking and visualization.",
              },
              {
                icon: <BookOpen className="mx-auto mb-2 text-white" size={32} />,
                title: "Multilingual Narration",
                desc: "Provides voice-over and translated celestial information in multiple regional languages.",
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                className="flex flex-col p-4 rounded-lg shadow-md feature-card" // Added padding and shadow
              >
                {card.icon}
                <h4 className="mb-2 text-lg font-semibold text-white sm:text-xl">{card.title}</h4> {/* Responsive text size */}
                <p className="flex-grow text-sm text-white sm:text-base">{card.desc}</p> {/* Responsive text size */}
              </motion.div>
            ))}
          </div>
          <div className="w-full py-3 text-center">
            {showSkyPreview && (
              <motion.div
                className="max-w-5xl mx-auto mt-4 overflow-hidden border border-gray-700 shadow-md rounded-xl"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <iframe
                  src="https://stellarium-web.org/"
                  title="Stellarium Web Preview"
                  className="w-full h-[300px] sm:h-[400px] md:h-[500px]" // Responsive iframe height
                  allowFullScreen
                ></iframe>
              </motion.div>
            )}
          </div>

          {/* 📘 Stellarium Guide Link */}
          <div className="mt-10 text-center stellarium-guide-container"> {/* Responsive margin-top and center text */}
            <h3 className="mb-4 text-lg font-medium text-white sm:text-xl"> {/* Responsive text size */}
              📖 To know more about Stellarium, follow the link below:
            </h3>
            <a
              href="https://stellarium.org/guide/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 text-base font-semibold text-white transition-colors duration-300 bg-blue-600 rounded-lg hover:bg-blue-700 sm:text-lg" // Responsive button
            >
              Open Stellarium Guide
            </a>
          </div>
        </section>
      </div>
    </PageWrapper>
  );
}